import type { Operation, RequestBody, ResponseBody } from '@platform/openapi';
import type { Order } from '../orders/orders.ts';

export const RETURNS_ROLE = 'inventory:write';

export type ReturnCandidate = ResponseBody<Operation<'/admin/returns/parcels', 'get'>>['items'][number];

export type ReturnReceiptsBody = RequestBody<Operation<'/admin/returns/receipts', 'post'>>;

export type DamagedCounts = Readonly<Record<string, number>>;

export type BasketEntry = {
  readonly order: Order;
  readonly announced: boolean;
  readonly counts: DamagedCounts;
  readonly refusal: string | null;
};

export const noDamage = (order: Order): DamagedCounts =>
  Object.fromEntries(order.lines.map((line) => [line.variantId, 0]));

export const basketEntryOf = (order: Order, announced: boolean): BasketEntry => ({
  order,
  announced,
  counts: noDamage(order),
  refusal: null,
});

export const isValidDamage = (damaged: number, quantity: number): boolean =>
  Number.isInteger(damaged) && damaged >= 0 && damaged <= quantity;

export const damagedOf = (counts: DamagedCounts, variantId: string): number => counts[variantId] ?? 0;

export const isEntryValid = (entry: BasketEntry): boolean =>
  entry.order.lines.every((line) => isValidDamage(damagedOf(entry.counts, line.variantId), line.quantity));

const linesOf = (entry: BasketEntry) =>
  entry.order.lines.map((line) => {
    const damaged = damagedOf(entry.counts, line.variantId);
    return { variantId: line.variantId, restockable: line.quantity - damaged, damaged };
  });

export const receiptsBodyOf = (entries: readonly BasketEntry[]): ReturnReceiptsBody => ({
  parcels: entries.map((entry) => ({
    orderId: entry.order.id,
    version: entry.order.version,
    lines: linesOf(entry),
  })),
});

export type ReceiptSummary = {
  readonly orderId: string;
  readonly number: string;
  readonly restocked: number;
  readonly damaged: number;
};

export const summaryOf = (entry: BasketEntry): ReceiptSummary => {
  const lines = linesOf(entry);
  return {
    orderId: entry.order.id,
    number: entry.order.number,
    restocked: lines.reduce((total, line) => total + line.restockable, 0),
    damaged: lines.reduce((total, line) => total + line.damaged, 0),
  };
};

export type ScanVerdict =
  | { readonly kind: 'add'; readonly candidate: ReturnCandidate }
  | { readonly kind: 'choose'; readonly candidates: readonly ReturnCandidate[] }
  | { readonly kind: 'already'; readonly candidate: ReturnCandidate }
  | { readonly kind: 'refused'; readonly candidate: ReturnCandidate }
  | { readonly kind: 'notFound' };

export const scanVerdictOf = (
  candidates: readonly ReturnCandidate[],
  inBasket: ReadonlySet<string>,
): ScanVerdict => {
  const [first] = candidates;
  if (first === undefined) return { kind: 'notFound' };
  const addable = candidates.filter((candidate) => candidate.receivable && !inBasket.has(candidate.order.id));
  const [only] = addable;
  if (addable.length > 1) return { kind: 'choose', candidates: addable };
  if (only !== undefined) return { kind: 'add', candidate: only };
  const scanned = candidates.find((candidate) => inBasket.has(candidate.order.id));
  return scanned === undefined
    ? { kind: 'refused', candidate: first }
    : { kind: 'already', candidate: scanned };
};
